import { Component, EventEmitter, Input, OnInit, Optional, Output, Self } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../ajs-upgraded-providers';
import { AnnotationsStorageService } from '../../annotations/core/annotations-storage.service';
import { DialogService } from '../dialogs/core/dialog.service';
import { Action } from '../menu/action';
import { UtilsService } from '../shared/core/utils.service';
import { ToggleDirective } from '../shared/shared/toggle.directive';
import { SearchResult } from '../shared/shared/types/search-result';
import { Visibility } from '../shared/shared/types/visibility.enum';
import { Comment } from './comment';

@Component({
  selector: 'rl-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss', './comment.component-rtl.css']
})
export class CommentComponent implements OnInit {
  @Input() comment: Comment;
  @Input() searchResults: SearchResult[] = [];
  @Input() searchResultsIndex: string;
  @Input() editMode: boolean;
  @Input() set visibilityAllowed(visibilityAllowed: 'all' | 'private' | 'public') {
    this._visibilityAllowed = visibilityAllowed;
    this.initActions();
  }
  @Input() hideMenu: boolean;
  @Input() privateIndication: boolean = true;
  @Output() onEditClicked = new EventEmitter<void>();
  @Output() onEditSaved = new EventEmitter<Comment>();
  @Output() onEditCanceled = new EventEmitter<void>();
  @Output() onDelete = new EventEmitter<Comment>();
  @Output() onDeleteCanceled = new EventEmitter<void>();
  @Output() onSetAsPublic = new EventEmitter<void>();
  @Output() onSetAsPrivate = new EventEmitter<void>();
  @Output() onReply = new EventEmitter<Comment>();

  visibilityOptions: string[] = ['PUBLIC', 'PRIVATE'];
  actions: (Action | string)[];
  newCommentReply: Comment;
  showReplies: boolean;
  _visibilityAllowed: 'all' | 'private' | 'public';

  constructor(
    private annStorage: AnnotationsStorageService,
    private authService: AuthService,
    private translate: TranslateService,
    private dialog: DialogService,
    @Self() @Optional() private toggle: ToggleDirective,
    private utilsService: UtilsService
  ) {}

  ngOnInit(): void {
    if (this.toggle) {
      this.toggle.onOpen = () => (this.showReplies = true);
      this.toggle.onClose = () => (this.showReplies = this.comment.subComments?.length < 3);
    }

    if (this.comment.isNew) {
      this.editMode = true;
    } else {
      this.sortReplies();
      this.refresh();
    }
  }

  clickMenu = (e) => this[e]();

  delete = () => {
    let msg = `${this.translate.instant('are.you.sure.delete.comment')}</br><b><i>"${this.comment.title}"</i></b>?`;
    if (this.comment.subComments?.length) msg += ` ${this.translate.instant('comment.replies.delete.msg')}`;
    this.dialog.openDeleteDialog(
      msg,
      () => this.onDelete.emit(this.comment),
      () => this.onDeleteCanceled.emit()
    );
  };

  setAsPublic = () => this.onSetAsPublic.emit();
  setAsPrivate = () => this.onSetAsPrivate.emit();

  initActions() {
    if (this.hideMenu) return;
    this.actions = [];

    if (!this.comment.user?.currentUser && !this.authService.isLibrarianOrInstructor()) return;

    if (this._visibilityAllowed == 'all') {
      let visibilityAction = new Action(this.comment.visibility == Visibility.PRIVATE ? 'setAsPublic' : 'setAsPrivate');
      if (this.comment.subComments?.length) {
        visibilityAction.tooltip = 'comment.changeVisibility.disabled';
        visibilityAction.disable = true;
      }
      this.actions.push(visibilityAction);
    }

    if (this.comment.user?.currentUser) {
      this.actions.push('edit');
    }

    this.actions.push('delete');
  }

  edit() {
    this.editMode = true;
    this.onEditClicked.emit();
  }

  editComment(e: Comment) {
    this.comment.title = e.title;
    this.onEditSaved.emit(e);
    this.editMode = false;
  }

  cancelEditComment() {
    this.onEditCanceled.emit();
    this.editMode = false;
  }

  getFieldMarks(fieldName: string) {
    return this.searchResults.filter((sr) => sr.objectIndex == this.searchResultsIndex && sr.objectField == fieldName).map((searchResult) => searchResult.strMarker);
  }

  isInstructorComment = () => !(this.comment.user?.role == 'student');

  reply() {
    this.newCommentReply = new Comment();
    this.newCommentReply.parentAttachedId = this.comment.attachedId;
    this.openReplies();
  }

  cancelNewReply = () => delete this.newCommentReply;

  saveNewReply(e: Comment) {
    this.onReply.emit(e);
    this.initActions();
    this.sortReplies();
    delete this.newCommentReply;
  }

  deleteReply(subComment: number | Comment) {
    if (subComment instanceof Comment) {
      this.onDelete.emit(subComment);
    } else {
      let comment = this.comment.subComments.splice(subComment, 1);
      this.onDelete.emit(comment[0]);
      this.initActions();
    }
  }

  cancelReply = (id: string) => {
    this._focusId(`menu-btn-comment-actions-${id}`);
  };

  private _focusId(id: string) {
    const elementToFocus = document.querySelector(`#${id}`);
    if (elementToFocus != null) {
      (elementToFocus as HTMLElement).focus();
    }
  }

  openReplies = () => this.toggle.open();
  saveReply = (subComment: Comment) => this.onEditSaved.emit(subComment);
  markAsPrivate = () => this.comment.visibility == 'PRIVATE' && this.privateIndication;

  toggleSelectComment(e: string) {
    if (!e) return;
    this.annStorage.scrollToAnnotation(e);
  }

  refresh() {
    this.initActions();
    this.showReplies = this.comment.subComments?.length < 3;
  }

  closeReplies() {
    if (this.comment.subComments?.length > 2) {
      this.showReplies = false;
    }
  }

  sortReplies() {
    this.utilsService.sortComplex(this.comment?.subComments, [{ field: 'date', dir: 'asc' }]);
  }
}
